<template>
    <div class="">
        <div v-if="this.isRunning(this.auction)">
            <div class="chart-inner-headline mb-4 h-16">{{ $t('shared.monitoring.next_price_drop.title') }}</div>
            <div v-if="isPriceDropAvailable">
                <div class="items-center mt-3">
                    {{ $t('shared.monitoring.next_price_drop.' + nextPriceDropMessage) }}
                    <div class="flex items-baseline mt-4">
                        <div class="w-1/2 block headline text-right mr-2">{{timeNextPriceDrop | formatTime($t('locale.formatTime'), tz.auctionTimeZone())}}</div>
                        <div class="w-1/2 block text-md-left">{{timeNextPriceDrop | formatTimeAmPm(tz.auctionTimeZone())}}</div>
                    </div>
                </div>
                <div class="mt-5 text-center mb-2" v-if="isPriceDropEditable">
                    <button @click="showAdjustNextPriceDropDialog()" type="button" class="btn btn-primary ml-auto">
                        {{ $t('shared.monitoring.next_price_drop.button_label') }}
                    </button>
                    <alert-message
                        v-if="showAdjustNextPriceDropFailed"
                        :error-type="ERROR_TYPE_ERROR"
                        :show-as-list="false"
                        :messages="[this.$t('pages.controlpanel.index.controls.producer_control.stop_live_show.stop_failed')]"
                        :display-errors-title="false"
                        @close="showAdjustNextPriceDropFailed = false"
                        class="sm:px-0 lg:px-0 pr-4 sm:pr-6 lg:pr-4"
                    />
                    <alert-message
                        v-if="showAdjustNextPriceDropSuccess"
                        :error-type="ERROR_TYPE_INFO"
                        :show-as-list="false"
                        :messages="[this.$t('shared.monitoring.next_price_drop.price_drop_time_changed')]"
                        :display-errors-title="false"
                        @close="showAdjustNextPriceDropSuccess = false"
                        class="sm:px-0 lg:px-0 pr-4 sm:pr-6 lg:pr-4"
                    />
                </div>
            </div>
            <div class="flex items-center mt-3" v-if="!isPriceDropAvailable">
                {{ $t('shared.monitoring.next_price_drop.no_next_price_drop') }}
            </div>

            <modal ref="stopLiveShowConfirmDialog"
                   @confirm="onSubmitAdjustNextPriceDrop"
                   @cancel="onCancelAdjustNextPriceDrop"
                   :dangerous="true"
                   :title="this.$t('shared.monitoring.next_price_drop.dialog.title')">
                <span class="">{{ $t('shared.monitoring.next_price_drop.dialog.change') }}</span>
                <div class="mt-5 flex items-center">
                    <div class="w-4/12 text-right pr-5">
                        {{ $t('shared.monitoring.next_price_drop.dialog.current_time', {timeZone: tz.code()}) }}
                    </div>
                    <div class="w-4/12 items-baseline flex">
                        <div class="block headline text-left mr-2">{{timeNextPriceDrop | formatTime($t('locale.formatTime'), tz.auctionTimeZone())}}</div>
                        <div class="block text-md-left">{{ timeNextPriceDrop | formatTimeAmPm(tz.auctionTimeZone()) }}</div>
                    </div>
                    <div class="w-4/12"></div>
                </div>
                <div class="mt-5 flex items-center">
                    <div class="w-4/12 text-right pr-5">
                        {{ $t('shared.monitoring.next_price_drop.dialog.new_price_drop_time', {timeZone: tz.code()}) }}
                    </div>
                    <div class="w-4/12 items-baseline flex">
                        <div class="block headline text-left mr-2">{{estimatedPriceDropTime | formatTime($t('locale.formatTime'), tz.auctionTimeZone())}}</div>
                        <div class="block text-md-left">{{estimatedPriceDropTime | formatTimeAmPm(tz.auctionTimeZone())}}</div>
                    </div>
                    <div class="w-4/12 flex">
                        <div class="w-1/2">
                            <button type="button" class="btn btn-secondary ml-2" v-if="!isNewTimeTooLow" @click="reduceNewTime">
                                <icon name="arrow-up" class="h-5 w-5 text-blue-500 rotate-180"></icon>
                            </button>
                        </div>
                        <div class="w-1/2">
                            <button type="button" class="btn btn-secondary" v-if="!isNewTimeTooHigh" @click="increaseNewTime">
                                <icon name="arrow-up" class="h-5 w-5 text-blue-500"></icon>
                            </button>
                        </div>
                    </div>
                </div>
            </modal>
        </div>
        <div v-else>
            <div class="chart-inner-headline mb-4 h-16">{{ $t('shared.monitoring.next_price_drop.title') }}</div>
            <div class="flex items-center mt-3">
                {{ $t('shared.monitoring.next_price_drop.sold_out') }}
            </div>
        </div>
    </div>
</template>

<script>

import Icon from "../Icon";
import Modal from "../Modals/Modal";
import {ErrorTypes} from '../../mixins/ErrorTypes';
import AlertMessage from "@/Shared/Alerts/AlertMessage";
import TimeZoneService from "../../Services/TimeZoneService";
import moment from 'moment';
import AuctionState from "@/mixins/AuctionState";

export default {
    components: {
        Icon,
        Modal,
        AlertMessage
    },
    mixins: [
        AuctionState,
        ErrorTypes
    ],
    props: {
        auction: Object,
        nextPriceDrop: Object,
        currentLiveShow: Object,
        nextLiveShow: Object
    },
    data() {
        return {
            newPriceDropMinutes: 0,
            sendingAdjustNextPriceDrop: false,
            showAdjustNextPriceDropFailed: false,
            showAdjustNextPriceDropSuccess: false,
            countDownUntilNextPriceDrop: null,
            isNewPriceDropTimeValid: true,
            nextPriceDropMessage: 'next_price_drop',
            isPriceDropEditable: false,
            liveShow: this.currentLiveShow || this.nextLiveShow
        }
    },
    computed: {
        tz() {
            return TimeZoneService;
        },
        minTime() {
            let current = moment().tz(this.tz.auctionTimeZone());
            let liveShowStartTime = moment.tz(this.liveShow?.date_start, this.tz.auctionTimeZone());
            return this.liveShow ? Math.max(current.valueOf(), liveShowStartTime.valueOf()) + 120000 : current.valueOf() + 120000;
        },
        maxTime() {
            let current = moment().tz(this.tz.auctionTimeZone());
            let liveShowEndTime = moment.tz(this.liveShow?.date_end, this.tz.auctionTimeZone());
            return this.liveShow ? liveShowEndTime.valueOf() - 120000 : current.valueOf();
        },
        timeNextPriceDrop() {
            return this.nextPriceDrop?.date_start;
        },
        minMinutes() {
            return parseInt(0.5 + ((new Date().valueOf() - this.minTime.valueOf()) / 60000));
        },
        maxMinutes() { // The number of minutes from now the start of the live show
            return ((this.maxTime - this.minTime) / 60000);
        },
        estimatedPriceDropTime() {
            return new Date(this.minTime + this.newPriceDropMinutes * 60000).toUTCString();
        },
        isNewTimeTooLow() {
            let current = new Date(this.minTime + (60000 * this.newPriceDropMinutes));
            return current.valueOf() <= this.minTime + 2;
        },
        isNewTimeTooHigh() {
            let current = new Date(this.minTime + 60000 * this.newPriceDropMinutes);
            return current.valueOf() >= this.maxTime - 1;
        },
        isPriceDropAvailable() {
            return this.update();
        }
    },
    methods: {
        reduceNewTime() {
            if (this.newPriceDropMinutes > this.maxMinutes) {
                this.newPriceDropMinutes = this.maxMinutes - 1;
            } else {
                this.newPriceDropMinutes = this.newPriceDropMinutes - 1;
            }
        },
        increaseNewTime() {
            if (this.newPriceDropMinutes < this.minMinutes) {
                this.newPriceDropMinutes = this.minMinutes + 1;
            } else {
                this.newPriceDropMinutes = this.newPriceDropMinutes + 1;
            }
        },
        adjustNextPriceDropFailedAlertClosed() {
            this.showAdjustNextPriceDropFailed = false;
        },
        onSubmitAdjustNextPriceDrop() {
            if (this.isNewPriceDropTimeValid && this.sendingAdjustNextPriceDrop !== true) {
                this.sendingAdjustNextPriceDrop = true;
                this.$refs.stopLiveShowConfirmDialog?.hide();
                this.showAdjustNextPriceDropFailed = false;

                let sentTime = moment.tz(this.estimatedPriceDropTime, this.tz.auctionTimeZone());
                axios.put(route('auctions.changeNextPriceDropTime',
                    {
                        auction: this.auction.id,
                        levelId: this.nextPriceDrop.id,
                        newPriceDropTime: sentTime.unix()
                    })
                )
                    .then((response) => {
                        if (response.data.status === 1) {
                            console.log(response.data.message);
                            this.showAdjustNextPriceDropSuccess = false;
                            this.showAdjustNextPriceDropFailed = true;
                        } else {
                            this.showAdjustNextPriceDropSuccess = true;
                            this.showAdjustNextPriceDropFailed = false;
                        }
                    })
                    .catch((err) => {
                        this.showAdjustNextPriceDropSuccess = false;
                        this.showAdjustNextPriceDropFailed = true;
                        console.log(err);
                    })
                    .finally(() => {
                        this.sendingAdjustNextPriceDrop = false;
                        this.$emit('auctionDataUpdated');
                    });
            }
        },
        onCancelAdjustNextPriceDrop() {
            this.$refs.stopLiveShowConfirmDialog?.hide();
        },
        showAdjustNextPriceDropDialog() {
            let startTime = moment.tz(this.nextPriceDrop?.date_start, this.tz.auctionTimeZone());
            let now = moment.tz(this.minTime, this.tz.auctionTimeZone());
            this.newPriceDropMinutes = Math.max(0, startTime.diff(now, 'minutes'));
            this.showAdjustNextPriceDropFailed = false;
            this.showAdjustNextPriceDropSuccess = false;
            this.$refs.stopLiveShowConfirmDialog?.show();
        },
        update() {
            // Show kommt aber läuft noch nicht
            if (!this.currentLiveShow && this.nextLiveShow) {
                this.nextPriceDropMessage = 'edit_for_next_show';
                this.liveShow = this.nextLiveShow;
                this.isPriceDropEditable = true;
                return true;
            }

            // show läuft
            if (this.currentLiveShow) {
                if (this.nextPriceDrop) {
                    let priceDropTime = moment.tz(this.nextPriceDrop.date_start, this.tz.auctionTimeZone()).valueOf();
                    let currentLiveShowStartTime = moment.tz(this.currentLiveShow.date_start, this.tz.auctionTimeZone()).valueOf();
                    let currentLiveShowEndTime = moment.tz(this.currentLiveShow.date_end, this.tz.auctionTimeZone()).valueOf();

                    // price drop kommt
                    if (priceDropTime > Date.now()) {
                        if (priceDropTime >= currentLiveShowStartTime && priceDropTime <= currentLiveShowEndTime) { // innerhalb der aktuellen Live show
                            this.nextPriceDropMessage = 'edit_for_current_show';
                            this.isPriceDropEditable = true;
                            this.liveShow = this.currentLiveShow;
                            return true;
                        }
                    }// else { // price hat stattgefunden
                    if (this.nextLiveShow) {
                        let nextLiveShowStartTime = moment.tz(this.nextLiveShow.date_start, this.tz.auctionTimeZone()).valueOf();
                        let nextLiveShowEndTime = moment.tz(this.nextLiveShow.date_end, this.tz.auctionTimeZone()).valueOf();
                        if (priceDropTime >= nextLiveShowStartTime && priceDropTime <= nextLiveShowEndTime) { // innerhalb der NÄCHSTEN Live show
                            this.nextPriceDropMessage = 'past_edit_next';
                            this.isPriceDropEditable = true;
                            this.liveShow = this.nextLiveShow;
                            return true;
                        }
                    } else {
                        this.nextPriceDropMessage = 'past_no_more';
                        this.isPriceDropEditable = false;
                        return true;
                    }
                } else {
                    this.nextPriceDropMessage = 'no_next_price_drop';
                    this.isPriceDropEditable = false;
                }
            } else {
                this.isPriceDropEditable = false;
            }
            this.liveShow = null;
            return false;
        }
    },
    watch: {
        nextPriceDrop: function () {
            this.update();
        },
        currentLiveShow: function () {
            this.update();
        },
        nextLiveShow: function () {
            this.update();
        }
    },
}
</script>
